import { useState, useCallback } from 'react';

type SortOrder = 'asc' | 'desc';

interface UseSortReturn<T extends string> {
    sortBy: T;
    sortOrder: SortOrder;
    toggleSort: (field: T) => void;
    setSortBy: (field: T) => void;
    setSortOrder: (order: SortOrder) => void;
}

/**
 * Custom hook for managing sort state with toggle functionality.
 * Clicking the same column toggles asc/desc, clicking a new column resets to asc.
 *
 * @param defaultField - The initial sort field
 * @param defaultOrder - The initial sort order (default: 'asc')
 */
export function useSort<T extends string>(
    defaultField: T,
    defaultOrder: SortOrder = 'asc'
): UseSortReturn<T> {
    const [sortBy, setSortBy] = useState<T>(defaultField);
    const [sortOrder, setSortOrder] = useState<SortOrder>(defaultOrder);

    const toggleSort = useCallback((field: T) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    }, [sortBy]);

    return {
        sortBy,
        sortOrder,
        toggleSort,
        setSortBy,
        setSortOrder,
    };
}
